import { useUiLocale } from "../../locale/UiLocale";
import { type KeyboardEvent, type MouseEvent, useRef, useState } from "react";
import { Icon } from "../../icons";
import { Popover } from "../Popover/Popover";
import { isActivationKey } from "./activationKeys";
import type { FilterOption } from "./DataTableTypes";
import { FilterMenu } from "./FilterMenu";
import { NO_FILTER } from "./filterOptions";

/** Minimum width of the options popover, in px. */
const FILTER_MENU_MIN_WIDTH = 200;

interface FilterPillProps {
  label: string;
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
}

/** A column filter: a pill that opens its options and shows the chosen one. */
export function FilterPill({ label, value, options, onChange }: FilterPillProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const active = value !== NO_FILTER;
  const current = options.find((o) => o.value === value);

  return (
    <span className="nb-filter-wrap">
      <button
        ref={anchorRef}
        type="button"
        className={`nb-filter${active ? " is-active" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {active ? null : <Icon name="plus" />}
        <span>{label}</span>
        {active ? <b>{current?.label ?? value}</b> : null}
        {active ? <FilterPillClear label={label} onClear={() => onChange(NO_FILTER)} /> : null}
      </button>
      <Popover
        anchorRef={anchorRef}
        open={open}
        onClose={() => setOpen(false)}
        align="start"
        role="listbox"
        ariaLabel={label}
        minWidth={FILTER_MENU_MIN_WIDTH}
      >
        <FilterMenu
          label={label}
          value={value}
          options={options}
          onPick={(picked) => {
            onChange(picked);
            setOpen(false);
          }}
        />
      </Popover>
    </span>
  );
}

/** The "x" inside an active pill; it clears the filter without opening the options. */
function FilterPillClear({ label, onClear }: { label: string; onClear: () => void }): React.JSX.Element {
  const locale = useUiLocale();
  function clearOnly(e: MouseEvent | KeyboardEvent): void {
    e.stopPropagation();
    onClear();
  }

  return (
    <span
      role="button"
      tabIndex={0}
      className="nb-filter-clear"
      aria-label={locale === "es" ? `Limpiar filtro de ${label.toLowerCase()}` : `Clear ${label.toLowerCase()} filter`}
      onClick={clearOnly}
      onKeyDown={(e) => {
        if (!isActivationKey(e.key)) return;
        e.preventDefault();
        clearOnly(e);
      }}
    >
      <Icon name="x" />
    </span>
  );
}
